import React, { useMemo, useState } from 'react';
import { useStores } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { Mail, MapPin, PhoneCall, Search, Phone, Inbox } from 'lucide-react';

function onlyDigits(phone = '') {
  return String(phone).replace(/[^\d+]/g, '');
}

const EC_STORES = ['events', 'venues', 'schedule'];

export default function EmergencyContacts() {
  const [query, setQuery] = useState('');

  const { events, venues, schedule, loading } = useStores(EC_STORES);

  // Derive contacts from the 3 stores — auto-recomputes when any store updates
  const contacts = useMemo(() => {
    const rows = [];
    events.forEach((event) => {
      if (event.primaryPocName || event.primaryPocPhone || event.primaryPocEmail) {
        rows.push({
          id: `event-${event.id}`,
          name: event.primaryPocName || 'Event POC',
          phone: event.primaryPocPhone,
          email: event.primaryPocEmail,
          context: event.name || 'Event',
          type: 'Event',
          location: event.venue,
        });
      }
    });
    venues.forEach((venue) => {
      if (venue.contactName || venue.contactPhone || venue.contactEmail) {
        rows.push({
          id: `venue-${venue.id}`,
          name: venue.contactName || 'Venue POC',
          phone: venue.contactPhone,
          email: venue.contactEmail,
          context: venue.name || 'Venue',
          type: venue.venueType || 'Venue',
          location: venue.address || venue.city,
        });
      }
    });
    schedule.filter((item) => item.published).forEach((item) => {
      if (item.pocName || item.pocPhone) {
        rows.push({
          id: `schedule-${item.id}`,
          name: item.pocName || 'Schedule POC',
          phone: item.pocPhone,
          context: item.title || 'Itinerary',
          type: item.type || 'Itinerary',
          location: item.location || item.conferenceRoom,
        });
      }
    });
    return rows;
  }, [events, venues, schedule]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return contacts;
    return contacts.filter((row) =>
      [row.name, row.phone, row.email, row.context, row.type, row.location].filter(Boolean).join(' ').toLowerCase().includes(needle)
    );
  }, [contacts, query]);

  if (loading) return <PageLoader label="Loading contacts…" sublabel="Fetching on-ground hotlines" />;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
          <Phone style={{ color: 'var(--accent)' }} size={24} />
          <span>On-Ground Hotlines</span>
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Instant corporate coordinators, emergency medical desks, and airport transport coordinators
        </p>
      </div>

      {/* Interactive Search Deck */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-500 uppercase">Search active coordinators</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3.5 text-slate-400" />
            <input 
              value={query} 
              onChange={(e) => setQuery(e.target.value)} 
              placeholder="Search by coordinator name, property, hotel address, or scheduled area..." 
              className="w-full bg-slate-100 border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:bg-white focus:border-slate-800 outline-none transition" 
            />
          </div>
        </div>
      </div>

      {/* Directory Cards */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">No coordinators found</h3>
          <p className="text-sm text-slate-500 mt-1">Add POC coordinates inside properties, events, or itinerary slots</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((contact) => (
            <div key={contact.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4 hover:border-slate-300 transition flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-start justify-between pb-2 border-b border-slate-100">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 tracking-tight">{contact.name}</h3>
                    <span className="text-xs text-slate-500 font-mono font-medium block mt-0.5">{contact.context}</span>
                  </div>
                  <span className="sh-badge sh-badge-success capitalize font-semibold tracking-wide">
                    {contact.type}
                  </span>
                </div>

                {contact.location && (
                  <p className="text-xs text-slate-650 flex items-start gap-1.5 leading-normal">
                    <MapPin size={12} className="text-slate-400 shrink-0 mt-0.5" />
                    <span>{contact.location}</span>
                  </p>
                )}
              </div>

              {/* Quick dials */}
              <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row gap-2">
                {contact.phone && (
                  <a 
                    href={`tel:${onlyDigits(contact.phone)}`} 
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 transition shadow-sm"
                  >
                    <PhoneCall size={12} />
                    <span>Call Mobile</span>
                  </a>
                )}
                {contact.email && (
                  <a 
                    href={`mailto:${contact.email}`} 
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-200 hover:border-slate-400 rounded-lg text-xs font-semibold text-slate-700 transition hover:text-slate-950 bg-white"
                  >
                    <Mail size={12} />
                    <span>Email Address</span>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
